import { useEffect, useRef, useState } from 'react';

const pains = [
  {
    icon: 'ri-hospital-line',
    question: 'Estou afastado pelo INSS e ainda estou doente',
    text: 'Mesmo com benefício negado ou cortado, você pode ter direito a indenização. Analisamos seu caso gratuitamente.',
  },
  {
    icon: 'ri-file-damage-line',
    question: 'A empresa não quer emitir a CAT',
    text: 'A Comunicação de Acidente de Trabalho é obrigatória. Podemos exigir judicialmente a emissão e proteger seus direitos.',
  },
  {
    icon: 'ri-scales-line',
    question: 'Tenho direito à indenização?',
    text: 'Acidentes e doenças causadas pelo trabalho geram direito a indenização por danos morais, materiais e estéticos.',
  },
  {
    icon: 'ri-mental-health-line',
    question: 'Burnout pode gerar processo trabalhista?',
    text: 'Sim. O esgotamento profissional reconhecido como doença ocupacional pode fundamentar ação indenizatória contra o empregador.',
  },
];

function PainCard({ icon, question, text, delay }: { icon: string; question: string; text: string; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative bg-brand-black/20 border-l-4 border-brand-gold rounded-2xl p-8 hover:-translate-y-2 transition-all duration-400 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-full border border-brand-gold/40 mb-6">
        <i className={`${icon} text-brand-gold text-xl`} />
      </div>
      <h3 className="font-sans text-white text-lg font-semibold leading-snug mb-3">{question}</h3>
      <p className="font-sans text-white/60 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

export default function PainSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.3 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-brand-green py-20 md:py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div
          ref={titleRef}
          className="text-center mb-14"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-brand-gold font-sans text-xs font-semibold tracking-widest uppercase">Você se identifica?</span>
          <h2 className="font-serif text-white text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Você está passando por isso?
          </h2>
          <p className="font-sans text-white/60 text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            Milhares de trabalhadores em São Paulo têm seus direitos violados todos os dias. Conheça as situações mais comuns.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pains.map((p, i) => (
            <PainCard key={i} {...p} delay={i * 120} />
          ))}
        </div>
      </div>
    </section>
  );
}
